import React, {
  useState,
  FormEventHandler,
  FormEvent,
  useEffect,
  useRef,
} from 'react';
import { Row, Input, Col, DatePicker, message as Message } from 'antd';
import { PageHeader } from '../layout/PageHeader';
import { SaveButton } from '../layout/GlobalButton';
import { saveCard } from '../../store/bank/actions';
import { createCardCustomer } from '../../store/payment/actions';
import { useDispatch, useSelector } from 'react-redux';
import { getAppGlobalState } from '../../store/app-globals/selectors';
import { GlobalModalHandler, GlobalModalClose } from '../layout/GlobalModal';
import { VerifyModal, SuccessModal } from './VerifyingModal';
import { prefixes } from './components/prefixes';
import moment from 'moment';
import Visa from '../../assets/images/svg/icons8-visa.svg';
import Mastercard from '../../assets/images/svg/icons8-mastercard.svg';
// import { saveCustomer } from '../../store/payment/actions';
import { getUserState } from '../../store/user/selector';
// import Discover from '../../assets/images/svg/icons8-discover.svg';
// import Amex from '../../assets/images/svg/icons8-amex.svg';

const monthFormat = 'MM/YY';
export interface FormnState {
  card: string;
  expYear: string;
  expMonth: string;
  cvc: string;
  maxLength: number;
  activeVisa: boolean; // TODO: Clean this up
  activeMastercard: boolean;
  cardtype: string;
  valid: any;
  error: object;
}

const initFormState: FormnState = {
  card: '',
  expYear: '',
  expMonth: '',
  cvc: '',
  maxLength: 16,
  activeVisa: false, // TODO: Clean this up
  activeMastercard: false,
  cardtype: '',
  valid: '',
  error: {},
};

export interface ImageLogo {
  type: string;
  alt: string;
  active: boolean;
}

export const CardLogo: React.FC<ImageLogo> = ({ type, alt, active }) => {
  let imgClass = 'cc-logo';

  if (active) {
    imgClass = 'cc-logo active';
  }

  return (
    <>
      <img src={type} alt={`${alt} credit card logo`} className={imgClass} />
    </>
  );
};

export const AddPayment: React.FC = () => {
  const dispatch = useDispatch();
  const { isLoading, hasError, errorMessage } = useSelector(getAppGlobalState);
  const { user } = useSelector(getUserState);
  const [errMess, setErrMess] = useState('');
  const isSendingReport = useRef(false);
  const [paymentDetails, setPaymentDetails] = useState<FormnState>(
    initFormState
  );
  const prevRef = useRef(paymentDetails);
  const details = {
    card: '',
    cvc: '',
    expMonth: '',
    expYear: '',
    maxLength: 16,
    activeVisa: false,
    activeMastercard: false,
    cardtype: '',
    valid: '',
    error: {},
  };

  const verifyNumber = () => {
    let sum = 0;
    let temp = 0;
    const cardNumberCopy = paymentDetails.card.split(' ').join('');
    const checkDigit = parseInt(
      paymentDetails.card
        .split(' ')
        .join('')
        .slice(-1)
    );
    const parity = cardNumberCopy.length % 2;

    for (let i = 0; i <= cardNumberCopy.length - 2; i++) {
      if (i % 2 === parity) {
        temp = +cardNumberCopy[i] * 2;
      } else {
        temp = +cardNumberCopy[i];
      }

      if (temp > 9) {
        temp -= 9;
      }

      sum += temp;
    }

    return (sum + checkDigit) % 10 === 0;
  };

  const ccformat = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const handleFieldChange = (field: keyof FormnState): FormEventHandler => (
    e: FormEvent<HTMLInputElement>
  ): void => {
    if (e.currentTarget.value !== '' && field === 'card') {
      setPaymentDetails({
        ...paymentDetails,
        [field]: ccformat(e.currentTarget.value),
      });
      if (e.currentTarget.value.length !== paymentDetails.maxLength) {
        setPaymentDetails({
          ...paymentDetails,
          valid: '',
          [field]: ccformat(e.currentTarget.value),
        });
      }
      return;
    } else if (field === 'cvc') {
      setPaymentDetails({
        ...paymentDetails,
        [field]: e.currentTarget.value,
      });
      return;
    } else if (field === 'expMonth') {
      setPaymentDetails({
        ...paymentDetails,
        [field]: e.currentTarget.value,
      });
      return;
    } else if (field === 'expYear') {
      setPaymentDetails({
        ...paymentDetails,
        [field]: e.currentTarget.value,
      });
      return;
    }
    setPaymentDetails({
      ...paymentDetails,
      ...details,
    });
  };

  // const handleDateChange = (date: any, dateString: string) => {
  //   console.log(dateString);
  //   setPaymentDetails({ ...paymentDetails, expiry: date });
  // };

  const getValidMessage = () => {
    if (paymentDetails.valid !== '') {
      return paymentDetails.valid ? 'Valid ✓' : 'Invalid ✖';
    }

    return '';
  };

  const purgeInactive = (firstCard: string, secondCard: string) => {
    setPaymentDetails({
      ...paymentDetails,
      [`active` + firstCard]: false,
      ['active' + secondCard]: true,
      cardtype: secondCard,
      valid: '',
    });
  };

  const determineType = (card: string) => {
    for (const key of prefixes) {
      for (const value of key[1]) {
        if (card.startsWith(value)) {
          /* TODO: Find a better way to manage this. */
          switch (key[0]) {
            case 'Visa':
              purgeInactive('Mastercard', 'Visa');
              break;
            case 'Mastercard':
              purgeInactive('Visa', 'Mastercard');
              break;
            default:
              break;
          }

          return;
        } else {
          setPaymentDetails({
            ...paymentDetails,
            ['active' + key[0]]: false,
            cardtype: '',
            valid: '',
          });
        }
      }
    }
  };

  const checkCardsType = () => {
    if (prevRef.current.card !== paymentDetails.card.split(' ').join('')) {
      determineType(paymentDetails.card.split(' ').join(''));
    }

    if (prevRef.current.cardtype !== paymentDetails.cardtype) {
      if (paymentDetails.cardtype !== '') {
        setPaymentDetails({
          ...paymentDetails,
          ['active' + paymentDetails.cardtype]: true,
        });
      }
    }

    if (
      prevRef.current.card.length !==
        paymentDetails.card.split(' ').join('').length &&
      paymentDetails.card.split(' ').join('').length ===
        paymentDetails.maxLength
    ) {
      setPaymentDetails({
        ...paymentDetails,
        valid: verifyNumber(),
      });
    }
  };

  const handleSaveButton = () => {
    if (
      paymentDetails.card === '' ||
      paymentDetails.cvc === '' ||
      paymentDetails.expMonth === '' ||
      paymentDetails.expYear === ''
      // paymentDetails.expiry === ''
    ) {
      Message.error('Please complete the fields');
      return;
    }
    if (!paymentDetails.valid) {
      return;
    }

    const card = {
      card: paymentDetails.card.split(' ').join(''),
      expMonth: paymentDetails.expMonth,
      expYear: paymentDetails.expYear,
      cvc: paymentDetails.cvc,
      email: user.email,
    };
    dispatch(createCardCustomer(card));
    isSendingReport.current = true;
    if (isSendingReport.current) {
      GlobalModalHandler({
        content: <VerifyModal />,
      });
    }
  };

  const submitCards = () => {
    if (isSendingReport.current && !isLoading && hasError) {
      console.log('aw error', errorMessage);
      Message.error('Somwething went wrong');
    }
    if (isSendingReport.current && !isLoading && !hasError) {
      GlobalModalHandler({
        content: <SuccessModal />,
      });
      setTimeout(() => {
        GlobalModalClose();
      }, 2000);
      setPaymentDetails(details);
    }
  };
  useEffect(submitCards, [isLoading]);
  useEffect(() => {
    if (paymentDetails.card !== '') {
      checkCardsType();
    }
  }, [paymentDetails.card, paymentDetails.cardtype]);
  return (
    <div className="bank-account-container">
      <PageHeader headerTitle="New Card" />
      <Row justify="center">
        <Col xs={24} sm={24} md={24} lg={24} className="payment-wrapper">
          <div className="payment-container">
            <span>Card Details</span>
          </div>
          <Col className="input-field-container">
            <Input
              maxLength={19}
              size="large"
              placeholder="xxxx xxxx xxxx xxxx"
              // prefix={
              //   <CardLogo
              //     type={paymentDetails.activeMastercard ? Mastercard : Visa}
              //     alt={paymentDetails.cardtype}
              //     active={
              //       paymentDetails.activeMastercard
              //         ? paymentDetails.activeMastercard
              //         : paymentDetails.activeVisa
              //     }
              //   />
              // }
              className="input-field"
              onChange={handleFieldChange('card')}
              value={paymentDetails.card}
            />
          </Col>
          <div className="error">
            <span
              className={paymentDetails.valid ? 'error valid' : 'error invalid'}
            >
              {getValidMessage()}
            </span>
          </div>
          <Row justify="start">
            <Col className="input-field-container">
              <Row>
                <div>
                  <Input
                    size="small"
                    placeholder="12"
                    className="em-input-field"
                    maxLength={2}
                    onChange={handleFieldChange('expMonth')}
                    value={paymentDetails.expMonth}
                  />
                </div>
                <div>
                  <span style={{ position: 'absolute', bottom: '15px' }}>
                    /
                  </span>
                </div>
                <div>
                  <Input
                    size="small"
                    placeholder="20"
                    className="ey-input-field"
                    maxLength={2}
                    onChange={handleFieldChange('expYear')}
                    value={paymentDetails.expYear}
                  />
                </div>
              </Row>
            </Col>
            <Col className="input-field-container">
              <Input
                size="middle"
                placeholder="123"
                className="cvv-input-field"
                maxLength={3}
                onChange={handleFieldChange('cvc')}
                value={paymentDetails.cvc}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      {errMess !== '' ? (
        <Row justify="start" style={{ marginLeft: '10px' }}>
          <div>
            <span style={{ color: 'red' }}>{errMess}</span>
          </div>
        </Row>
      ) : null}

      <div className="savebutton-container">
        <SaveButton onclick={handleSaveButton} />
      </div>
    </div>
  );
};
